import { Button } from '@components/Button'
import ConfirmationModal from '@components/ConfirmationModal'
import { isNotFound } from '@lib/apiError'
import { queryKeys } from '@lib/queryKeys'
import type { GetUserType } from '@orbit/api-types'
import { useQueryClient } from '@tanstack/react-query'
import axios from 'axios'
import React, { useState } from 'react'
import toast from 'react-hot-toast'
import { announceBookingMembershipChange } from './bookingEvents'
import { setAccommodationGuest, setFlightPassenger } from './chatApi'
import {
	accommodationCard,
	bookingMembership,
	flightGroupCard,
	membershipButtonTitle,
	membershipLeaveMessage,
	membershipLeaveTitle,
	type BookingKind,
} from './core'

interface BookingMembershipButtonProps {
	tripId: string
	kind: BookingKind
	/** The flight group's or accommodation's id. */
	bookingId: number
	/** Who's on it now: the passengers on any leg, or the guests. */
	peopleIds: readonly number[]
	myUserId: number | undefined
	/** Who the people on it are, by id, to show them on its cards. */
	members: readonly Pick<GetUserType, 'id' | 'firstName' | 'lastName' | 'profilePicture'>[]
	className?: string
}

const failureMessage = (error: unknown, kind: BookingKind): string => {
	const noun = kind === 'flight' ? 'flight' : 'accommodation'
	if (axios.isAxiosError(error) && !error.response) return 'No internet. Check your connection and try again.'
	if (isNotFound(error)) return `This ${noun} was removed.`
	return `Couldn't update the ${noun}. Please try again later.`
}

/**
 * "I'm on this flight too" or "I'm staying here", and once I'm on it, "You're … ✓",
 * which takes me off after I confirm. Joining adds me to every leg of a flight booking.
 * Each is a single add or remove, so two members joining at once both end up on it.
 * Ported from iOS `BookingMembershipButton` and `BookingsFeature.toggleMembership`.
 */
const BookingMembershipButton: React.FC<BookingMembershipButtonProps> = ({
	tripId,
	kind,
	bookingId,
	peopleIds,
	myUserId,
	members,
	className,
}) => {
	const queryClient = useQueryClient()
	const [isConfirmingLeave, setIsConfirmingLeave] = useState(false)
	const [isSaving, setIsSaving] = useState(false)
	const membership = bookingMembership(kind, peopleIds, myUserId)

	const setMembership = async (isJoining: boolean) => {
		setIsSaving(true)
		try {
			const people =
				kind === 'flight'
					? flightGroupCard(await setFlightPassenger(tripId, bookingId, isJoining), members).people
					: accommodationCard(await setAccommodationGuest(tripId, bookingId, isJoining), members).people
			// Joining posts no Message: every stream on screen updates its cards of it in place.
			announceBookingMembershipChange({ kind: kind === 'flight' ? 'FlightGroup' : 'Accommodation', id: bookingId }, people)
			await Promise.all([
				queryClient.invalidateQueries({
					queryKey: kind === 'flight' ? queryKeys.flights(tripId) : queryKeys.accommodations(tripId),
				}),
				queryClient.invalidateQueries({ queryKey: queryKeys.tripOverview(tripId) }),
			])
		} catch (error) {
			toast.error(failureMessage(error, kind))
		} finally {
			setIsSaving(false)
		}
	}

	return (
		<>
			<Button
				type="button"
				className={className}
				variant={membership.isJoined ? 'tonal' : kind === 'flight' ? 'secondary' : 'primary'}
				disabled={isSaving || myUserId === undefined}
				onClick={() => (membership.isJoined ? setIsConfirmingLeave(true) : void setMembership(true))}
			>
				{membershipButtonTitle(membership)}
			</Button>
			<ConfirmationModal
				isOpen={isConfirmingLeave}
				onClose={() => setIsConfirmingLeave(false)}
				onConfirm={() => {
					setIsConfirmingLeave(false)
					void setMembership(false)
				}}
				title={membershipLeaveTitle(membership)}
				message={membershipLeaveMessage(membership)}
				confirmText="Leave"
				cancelText="Cancel"
				variant="danger"
			/>
		</>
	)
}

export default BookingMembershipButton
